/**
 * 全局项目设置缓存 Model
 *
 * 在项目管理员登录进入 dashboard 后批量加载所有管理项目的设置，
 * 缓存在全局 Map 中，各页面通过 useModel('useProjectSettings') 访问。
 * 整个登录会话期间只发起一次批量请求。
 */

import { request, useModel } from '@umijs/max';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getProjectSettings } from '@/pages/dashboard/workplace/org-admin/service';
import type { ProjectSettingsResponse } from '@/types/projectSettings';
import { createLogger } from '@/utils/logger';

const logger = createLogger('useProjectSettings');

type LoadState = 'idle' | 'loading' | 'loaded' | 'error';

export default function useProjectSettings() {
  const { initialState } = useModel('@@initialState');
  const currentUser = initialState?.currentUser;

  const [cache, setCache] = useState<Map<number, ProjectSettingsResponse>>(
    new Map(),
  );
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const loadStateRef = useRef<LoadState>('idle');

  const loadAllProjectSettings = useCallback(async () => {
    if (loadStateRef.current === 'loading' || loadStateRef.current === 'loaded')
      return;

    loadStateRef.current = 'loading';
    setLoadState('loading');
    try {
      const projectsRes = await request<{
        status: string;
        data: Array<{ project?: { id: number }; id?: number }>;
      }>('/v1/users/me/projects', { method: 'GET' });

      if (projectsRes.status !== 'success' || !projectsRes.data) {
        loadStateRef.current = 'error';
        setLoadState('error');
        return;
      }

      const projectIds: number[] = [];
      projectsRes.data.forEach((item) => {
        const id = item.project?.id ?? (item as any).id;
        if (id) projectIds.push(id);
      });

      if (projectIds.length === 0) {
        loadStateRef.current = 'loaded';
        setLoadState('loaded');
        return;
      }

      const results = await Promise.allSettled(
        projectIds.map((id) => getProjectSettings(id)),
      );

      const newCache = new Map<number, ProjectSettingsResponse>();
      const succeededIds: number[] = [];
      const failedIds: number[] = [];
      results.forEach((result, i) => {
        const pid = projectIds[i];
        if (result.status === 'fulfilled' && result.value.success) {
          if (pid !== undefined) {
            newCache.set(pid, result.value.data);
            succeededIds.push(pid);
          }
        } else if (pid !== undefined) {
          failedIds.push(pid);
        }
      });

      if (succeededIds.length > 0) {
        logger.info('获取项目设置成功', {
          projectIds: succeededIds,
          count: succeededIds.length,
        });
      }
      if (failedIds.length > 0) {
        logger.error('获取项目设置失败', {
          projectIds: failedIds,
          count: failedIds.length,
        });
      }

      setCache(newCache);
      loadStateRef.current = 'loaded';
      setLoadState('loaded');
    } catch (error) {
      logger.error('加载项目设置失败', error);
      loadStateRef.current = 'error';
      setLoadState('error');
    }
  }, []);

  const getSettings = useCallback(
    (
      projectId: number | string | undefined,
    ): ProjectSettingsResponse | undefined => {
      if (!projectId) return undefined;
      return cache.get(Number(projectId));
    },
    [cache],
  );

  const clearCache = useCallback(() => {
    setCache(new Map());
    loadStateRef.current = 'idle';
    setLoadState('idle');
    logger.info('项目设置缓存已清除');
  }, []);

  // 监听 currentUser 变化：登出时（变为 undefined）自动清除缓存
  const prevUserRef = useRef(currentUser);
  useEffect(() => {
    if (prevUserRef.current && !currentUser) {
      clearCache();
    }
    prevUserRef.current = currentUser;
  }, [currentUser, clearCache]);

  return {
    cache,
    loading: loadState === 'loading',
    loaded: loadState === 'loaded',
    loadState,
    loadAllProjectSettings,
    getSettings,
    clearCache,
  };
}
